import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { useLang } from './i18n';
import { openAnalyticsPreferences, trackEvent } from './analytics';
import './PortfolioV2.css';

const languages = ['fr', 'en', 'pt'];

const copy = {
  fr: {
    role: 'Développeur web freelance',
    navWork: 'Projets',
    navMethod: 'Méthode',
    navContact: 'Contact',
    navEstimate: 'Estimer mon projet',
    location: 'Partout en France · À distance',
    availability: 'Disponible pour de nouveaux projets',
    heroKicker: 'Sites web sur mesure · RDV sur votre site · Outils de gestion',
    heroTitle: 'Vous y avez mis une part de vous.',
    heroPunchline: 'Ça mérite de se voir.',
    heroText: 'Je crée des sites qui mettent votre activité en valeur. Vos clients peuvent y découvrir vos prestations, prendre rendez-vous ou vous envoyer une demande. Et vous gagnez du temps grâce à des outils de gestion adaptés.',
    heroPrimary: 'Parlons de votre projet',
    heroSecondary: 'Estimer mon projet',
    heroFoot: 'Un site existe déjà ? Je peux aussi le reprendre, sans tout recommencer.',
    boardLabel: 'Exemple de parcours',
    boardTitle: 'Vos clients prennent rendez-vous sur votre site.',
    boardNote: 'Par exemple, pour une séance ou une prestation : le client choisit un créneau disponible et reçoit une confirmation, sans échange de messages.',
    problemsKicker: 'Point de départ',
    problemsTitle: 'Et tout ce que votre site pourrait faire pour vous.',
    problemsIntro: 'Vos clients choisissent un rendez-vous pour vos prestations, directement sur votre site. Ils peuvent verser un acompte si besoin. Vous retrouvez leurs informations au même endroit. Quel serait votre premier soulagement ?',
    problems: [
      { id: 'booking', title: 'Les rendez-vous passent encore par messages', text: 'Sur votre site, les clients choisissent une prestation et un créneau disponible. Vous gérez les confirmations et les acomptes si nécessaire.', outcome: 'Moins d’allers-retours, un client sait quoi faire.', flow: ['Prestation', 'Créneau', 'Acompte si utile', 'Confirmation'] },
      { id: 'sell', title: 'Vous voulez vendre ou encaisser en ligne', text: 'Une boutique ou un paiement simple peut être intégré sans transformer le projet en usine à gaz.', outcome: 'Le client peut acheter ou payer au bon moment.', flow: ['Offre', 'Panier / choix', 'Paiement', 'Suivi'] },
      { id: 'manage', title: 'Votre gestion est dispersée', text: 'Clients, stock, commandes ou documents peuvent être centralisés dans un outil adapté à votre façon de travailler.', outcome: 'Une seule source de vérité au lieu de plusieurs fichiers.', flow: ['Données', 'Actions', 'Automations', 'Vue claire'] },
      { id: 'finish', title: 'Le projet existe, mais il n’arrive pas au bout', text: 'Je peux reprendre un projet commencé avec une IA, du no-code ou un autre développeur et prioriser ce qui manque pour le lancer.', outcome: 'On garde ce qui fonctionne et on débloque la suite.', flow: ['Audit', 'Priorités', 'Corrections', 'Mise en ligne'] },
    ],
    workKicker: '4 univers · 4 problèmes différents',
    workTitle: 'Votre activité a sa personnalité. Votre site devrait l’avoir aussi.',
    workIntro: 'Pas de recette visuelle copiée d’un client à l’autre. Je cherche ce qui fait votre singularité, puis je construis un site à sa mesure.',
    projectOpen: 'Voir le projet',
    projectCase: 'Découvrir le projet',
    reviewsKicker: 'Retours clients vérifiables',
    reviewsTitle: 'Ce sont mes clients qui en parlent le mieux.',
    reviewsIntro: 'Des retours publiés sur Codeur.com, consultables à leur source.',
    reviewsSource: 'Lire les recommandations sur Codeur.com',
    reviewsProfiles: 'Retrouver mon profil sur',
    takeoverKicker: 'Vous avez déjà un site ?',
    takeoverTitle: 'Vous avez déjà construit quelque chose. Pas question de tout effacer.',
    takeoverText: 'Votre site ne vous ressemble plus, votre projet n’avance plus ou votre développeur n’est plus disponible ? Je commence par comprendre ce qui existe. Ce qui fonctionne mérite peut-être d’être gardé.',
    takeoverNotes: [
      ['D’abord, regarder.', 'Le site, le code, les accès, les vrais blocages. Pas de refonte proposée à l’aveugle.'],
      ['Ensuite, choisir.', 'Ce qu’on garde, ce qu’on corrige, ce qu’on améliore. Avec des priorités et un devis lisible.'],
      ['Enfin, vous laisser libre.', 'Un site que vous pouvez administrer, et un projet qu’un autre professionnel pourra reprendre.'],
    ],
    takeoverPrimary: 'Reprendre ou améliorer mon site',
    takeoverSecondary: 'Raconter ma situation',
    proofKicker: 'Ce que je cherche à produire',
    proofTitle: 'Je conçois des sites aussi soignés à utiliser qu’à regarder.',
    proofItems: [
      ['Compréhensible', 'Un visiteur sait rapidement ce que vous faites et quoi faire ensuite.'],
      ['Administrable', 'Les tâches courantes ne doivent pas nécessiter de toucher au code.'],
      ['Évolutif', 'On lance une première version cohérente sans bloquer les besoins de demain.'],
      ['Reprenable', 'Code, accès et documentation restent propres pour ne pas vous enfermer.'],
    ],
    methodKicker: 'La méthode',
    methodTitle: 'Avant de toucher au code, comprendre votre métier.',
    methodSteps: [
      ['01', 'Comprendre', 'Vous m’expliquez votre activité, ce qui bloque et ce qui vous fait perdre du temps.'],
      ['02', 'Choisir', 'Je réduis le projet à une première version utile, avec un budget et des priorités compréhensibles.'],
      ['03', 'Construire', 'Je développe, teste et vous montre régulièrement ce qui avance.'],
      ['04', 'Livrer', 'Mise en ligne, accès, passation et suite possible sans dépendance inutile.'],
    ],
    estimateKicker: 'Pas encore sûr du périmètre ?',
    estimateTitle: 'Construisez votre première estimation en quelques minutes.',
    estimateText: 'Le configurateur vous donne des idées de fonctionnalités, explique leur impact et calcule une fourchette qui évolue réellement avec vos choix.',
    estimateCta: 'Tester l’estimateur',
    aboutKicker: 'Derrière le site',
    aboutTitle: 'Alioune Seck',
    aboutText: 'Je suis Alioune, développeur web indépendant basé en Bretagne. J’aime les activités qui ont une histoire, les détails qui changent tout et les sites dont les clients peuvent être fiers. Je crée aussi les outils qui facilitent leur travail, sans les rendre dépendants de moi.',
    aboutSmall: 'React · Next.js · Django · Python · PostgreSQL · API · Stripe',
    contactKicker: 'Le prochain mouvement',
    contactTitle: 'Racontez-moi ce que vous avez en tête.',
    contactText: 'Une idée, un site qui ne vous ressemble plus, un projet à reprendre ? Quelques lignes suffisent pour commencer. Je vous réponds personnellement.',
    name: 'Votre nom',
    email: 'Votre email',
    message: 'Décrivez le besoin en quelques lignes',
    send: 'Envoyer',
    sending: 'Envoi…',
    success: 'Message envoyé. Je reviens vers vous rapidement.',
    error: 'L’envoi a échoué. Vous pouvez aussi écrire à contact@alioune-seck.dev.',
    emailDirect: 'contact@alioune-seck.dev',
    footer: 'Basé à Quimper · Projets partout en France',
    footerExplore: 'Explorer',
    footerFind: 'Retrouvez-moi',
    footerLegal: 'Informations légales',
    localLink: 'Création site internet Quimper',
    nationalLink: 'France / à distance',
    legalMentions: 'Mentions légales',
    legalPrivacy: 'Confidentialité',
    legalTerms: 'CGU',
    legalSales: 'CGV',
    cookieSettings: 'Gérer les cookies',
    privacyForm: 'Les informations envoyées ici servent uniquement à répondre à votre demande.',
    privacyMore: 'En savoir plus',
  },
  en: {
    role: 'Freelance web developer',
    navWork: 'Work',
    navMethod: 'Method',
    navContact: 'Contact',
    navEstimate: 'Estimate my project',
    location: 'Across France · Remote',
    availability: 'Available for new projects',
    heroKicker: 'Custom websites · Client appointments on your site · Business tools',
    heroTitle: 'You’ve poured a part of yourself into it.',
    heroPunchline: 'It deserves to show.',
    heroText: 'I build websites that reflect your business. Customers can discover your services, book an appointment or send an enquiry on your site. Tailored tools make everyday management easier.',
    heroPrimary: 'Let’s talk about your project',
    heroSecondary: 'Estimate my project',
    heroFoot: 'Already have a website? I can take it over without starting from scratch.',
    boardLabel: 'Example flow',
    boardTitle: 'Your customers book appointments on your website.',
    boardNote: 'For a service or session, customers pick an available time and receive confirmation without back-and-forth messages.',
    problemsKicker: 'Starting point',
    problemsTitle: 'And everything your website could do for you.',
    problemsIntro: 'Appointments booked while you work. Deposits collected without chasing transfers. Customers in one place. Where would you start?',
    problems: [
      { id: 'booking', title: 'Appointments still happen through messages', text: 'Availability, booking, deposit and confirmations can live in one flow.', outcome: 'Less back-and-forth, a clear next step for the customer.', flow: ['Availability', 'Booking', 'Deposit', 'Confirmation'] },
      { id: 'sell', title: 'You want to sell or collect payment online', text: 'A store or simple payment flow can be added without turning the project into a monster.', outcome: 'Customers can buy or pay at the right moment.', flow: ['Offer', 'Choice', 'Payment', 'Follow-up'] },
      { id: 'manage', title: 'Your management is scattered', text: 'Customers, inventory, orders or documents can be brought into one tool.', outcome: 'One clear source instead of several files.', flow: ['Data', 'Actions', 'Automation', 'Clear view'] },
      { id: 'finish', title: 'The project exists but never reaches launch', text: 'I can take over work started with AI, no-code or another developer and prioritize what is missing.', outcome: 'Keep what works, unblock what does not.', flow: ['Audit', 'Priorities', 'Fixes', 'Launch'] },
    ],
    workKicker: '4 worlds · 4 different problems',
    workTitle: 'Your business has its own character. Your website should too.',
    workIntro: 'No visual formula copied from one client to the next. I look for what makes you different, then build around it.',
    projectOpen: 'View project',
    projectCase: 'Explore this project',
    reviewsKicker: 'Client feedback',
    reviewsTitle: 'Hear it from the people I work with.',
    reviewsIntro: 'Original reviews published in French on Codeur.com.',
    reviewsSource: 'Read reviews on Codeur.com',
    reviewsProfiles: 'Find my profile on',
    takeoverKicker: 'Already have a website?',
    takeoverTitle: 'You have already built something. We do not have to erase it.',
    takeoverText: 'Your site no longer feels right, your project is stuck or your developer is unavailable? I start by understanding what is already there. What works may be worth keeping.',
    takeoverNotes: [
      ['First, take a look.', 'The site, code, access and actual blockers. No blind redesign.'],
      ['Then, make choices.', 'What stays, what needs fixing and what could improve. Clear priorities and pricing.'],
      ['Finally, keep your freedom.', 'A site you can manage and a project another professional can take over.'],
    ],
    takeoverPrimary: 'Improve or take over my website',
    takeoverSecondary: 'Tell me what happened',
    proofKicker: 'What I aim to build',
    proofTitle: 'I build websites that feel as good to use as they look.',
    proofItems: [
      ['Clear', 'Visitors quickly understand what you do and what to do next.'],
      ['Manageable', 'Routine work should not require touching code.'],
      ['Evolvable', 'Ship a coherent first version without blocking tomorrow.'],
      ['Handover-ready', 'Code, access and documentation stay clean.'],
    ],
    methodKicker: 'Method',
    methodTitle: 'Understand your work before writing code.',
    methodSteps: [
      ['01', 'Understand', 'You explain the business, the blocker and what currently wastes time.'],
      ['02', 'Choose', 'I reduce the idea to a useful first version with clear priorities and budget.'],
      ['03', 'Build', 'I develop, test and show progress regularly.'],
      ['04', 'Deliver', 'Launch, access, handover and a clear path for what comes next.'],
    ],
    estimateKicker: 'Not sure about the scope yet?',
    estimateTitle: 'Build a first estimate in a few minutes.',
    estimateText: 'The configurator suggests useful features, explains their impact and changes the range as you make choices.',
    estimateCta: 'Try the estimator',
    aboutKicker: 'Behind the site',
    aboutTitle: 'Alioune Seck',
    aboutText: 'Full-stack developer based in Quimper. I mainly work with independent professionals and small businesses that need a website, business tool or an existing project brought back on track.',
    aboutSmall: 'React · Next.js · Django · Python · PostgreSQL · API · Stripe',
    contactKicker: 'Next move',
    contactTitle: 'Tell me what you have in mind.',
    contactText: 'An idea, a website that no longer fits, a project to take over? A few lines are enough to start. I reply personally.',
    name: 'Your name',
    email: 'Your email',
    message: 'Describe the need in a few lines',
    send: 'Send',
    sending: 'Sending…',
    success: 'Message sent. I will get back to you shortly.',
    error: 'Sending failed. You can also email contact@alioune-seck.dev.',
    emailDirect: 'contact@alioune-seck.dev',
    footer: 'Based in Quimper · Projects across France',
    footerExplore: 'Explore',
    footerFind: 'Find me online',
    footerLegal: 'Legal information',
    localLink: 'Web design Quimper',
    nationalLink: 'France / remote',
    legalMentions: 'Legal notice',
    legalPrivacy: 'Privacy',
    legalTerms: 'Terms',
    legalSales: 'Sales terms',
    cookieSettings: 'Cookie settings',
    privacyForm: 'The information sent here is only used to answer your request.',
    privacyMore: 'Learn more',
  },
  pt: {
    role: 'Desenvolvedor web freelance',
    navWork: 'Projetos',
    navMethod: 'Método',
    navContact: 'Contato',
    navEstimate: 'Estimar projeto',
    location: 'Toda a França · À distância',
    availability: 'Disponível para novos projetos',
    heroKicker: 'Sites sob medida · Agendamentos dos clientes no seu site · Gestão',
    heroTitle: 'Você colocou uma parte de si nesse trabalho.',
    heroPunchline: 'Isso merece aparecer.',
    heroText: 'Crio sites que valorizam a sua atividade. Os clientes podem conhecer os serviços, marcar um horário ou enviar um pedido diretamente no site. Ferramentas de gestão simplificam o seu dia a dia.',
    heroPrimary: 'Vamos conversar sobre o projeto',
    heroSecondary: 'Estimar meu projeto',
    heroFoot: 'Já tem um site? Posso continuar o projeto sem recomeçar do zero.',
    boardLabel: 'Exemplo de percurso',
    boardTitle: 'Os seus clientes marcam horários diretamente no site.',
    boardNote: 'Para uma sessão ou serviço, o cliente escolhe um horário disponível e recebe a confirmação, sem longas trocas de mensagens.',
    problemsKicker: 'Ponto de partida',
    problemsTitle: 'E tudo o que seu site poderia fazer por você.',
    problemsIntro: 'Agendamentos enquanto você trabalha. Sinais pagos sem cobrar por mensagem. Clientes organizados num só lugar. Por onde começar?',
    problems: [
      { id: 'booking', title: 'Agendamentos ainda por mensagem', text: 'Disponibilidade, reserva, sinal e confirmação no mesmo percurso.', outcome: 'Menos trocas e um próximo passo claro.', flow: ['Disponibilidade', 'Reserva', 'Sinal', 'Confirmação'] },
      { id: 'sell', title: 'Quero vender ou receber online', text: 'Loja ou pagamento simples sem complicar o projeto.', outcome: 'O cliente paga no momento certo.', flow: ['Oferta', 'Escolha', 'Pagamento', 'Acompanhamento'] },
      { id: 'manage', title: 'Minha gestão está espalhada', text: 'Clientes, estoque, pedidos ou documentos em uma ferramenta.', outcome: 'Uma fonte clara em vez de vários arquivos.', flow: ['Dados', 'Ações', 'Automação', 'Visão'] },
      { id: 'finish', title: 'O projeto existe mas não termina', text: 'Retomo um projeto iniciado com IA, no-code ou outro desenvolvedor.', outcome: 'Mantemos o que funciona e destravamos o resto.', flow: ['Auditoria', 'Prioridades', 'Correções', 'Publicação'] },
    ],
    workKicker: '4 universos · 4 problemas',
    workTitle: 'Seu trabalho tem personalidade. Seu site também deveria ter.',
    workIntro: 'Sem repetir o visual de um cliente para outro. Procuro o que torna sua atividade única e desenvolvo a partir disso.',
    projectOpen: 'Ver projeto',
    projectCase: 'Conhecer o projeto',
    reviewsKicker: 'Depoimentos de clientes',
    reviewsTitle: 'A experiência dos meus clientes, em suas palavras.',
    reviewsIntro: 'Avaliações originais em francês publicadas no Codeur.com.',
    reviewsSource: 'Ler avaliações no Codeur.com',
    reviewsProfiles: 'Veja meu perfil no',
    takeoverKicker: 'Já tem um site?',
    takeoverTitle: 'Você já construiu alguma coisa. Não precisamos apagar tudo.',
    takeoverText: 'O site já não representa sua atividade, o projeto parou ou o desenvolvedor não está disponível? Primeiro entendo o que já existe. O que funciona talvez mereça ser preservado.',
    takeoverNotes: [
      ['Primeiro, analisar.', 'O site, código, acessos e bloqueios reais. Nada de refazer sem avaliar.'],
      ['Depois, decidir.', 'O que manter, corrigir e melhorar. Prioridades e orçamento claros.'],
      ['Por fim, manter sua liberdade.', 'Um site que você pode gerir e um projeto que outro profissional pode continuar.'],
    ],
    takeoverPrimary: 'Melhorar ou retomar meu site',
    takeoverSecondary: 'Contar minha situação',
    proofKicker: 'O que eu busco construir',
    proofTitle: 'Crio sites tão cuidados no uso quanto na aparência.',
    proofItems: [
      ['Claro', 'O visitante entende rápido o que fazer.'],
      ['Administrável', 'O dia a dia não exige tocar no código.'],
      ['Evolutivo', 'Uma primeira versão que pode crescer.'],
      ['Transferível', 'Código, acessos e documentação limpos.'],
    ],
    methodKicker: 'Método',
    methodTitle: 'Entender o seu trabalho antes de escrever código.',
    methodSteps: [
      ['01', 'Entender', 'Você explica a atividade, o bloqueio e o que perde tempo.'],
      ['02', 'Escolher', 'Reduzo a ideia a uma primeira versão útil.'],
      ['03', 'Construir', 'Desenvolvo, testo e mostro o progresso.'],
      ['04', 'Entregar', 'Publicação, acessos e continuidade clara.'],
    ],
    estimateKicker: 'Ainda não sabe o escopo?',
    estimateTitle: 'Monte uma primeira estimativa em poucos minutos.',
    estimateText: 'O configurador sugere funções e muda a faixa conforme suas escolhas.',
    estimateCta: 'Testar estimador',
    aboutKicker: 'Por trás do site',
    aboutTitle: 'Alioune Seck',
    aboutText: 'Desenvolvedor full-stack baseado em Quimper. Trabalho principalmente com independentes e pequenas empresas.',
    aboutSmall: 'React · Next.js · Django · Python · PostgreSQL · API · Stripe',
    contactKicker: 'Próximo movimento',
    contactTitle: 'Conte-me o que tem em mente.',
    contactText: 'Uma ideia, um site a melhorar, um projeto para retomar? Algumas linhas bastam para começar. Respondo pessoalmente.',
    name: 'Seu nome',
    email: 'Seu email',
    message: 'Descreva a necessidade',
    send: 'Enviar',
    sending: 'Enviando…',
    success: 'Mensagem enviada.',
    error: 'Falha no envio. Você também pode escrever para contact@alioune-seck.dev.',
    emailDirect: 'contact@alioune-seck.dev',
    footer: 'Em Quimper · Projetos em toda a França',
    footerExplore: 'Explorar',
    footerFind: 'Redes profissionais',
    footerLegal: 'Informações legais',
    localLink: 'Criação de sites Quimper',
    nationalLink: 'França / remoto',
    legalMentions: 'Aviso legal',
    legalPrivacy: 'Privacidade',
    legalTerms: 'Termos',
    legalSales: 'Condições de venda',
    cookieSettings: 'Gerir cookies',
    privacyForm: 'As informações enviadas aqui são usadas apenas para responder ao seu pedido.',
    privacyMore: 'Saiba mais',
  },
};

const projects = [
  {
    id: 'nuvo',
    title: 'NÜVO',
    eyebrow: 'Studio créatif · expérience web',
    image: '/projects-v2/nuvo.webp',
    href: 'https://xn--nvo-hoa.fr/',
    tone: 'violet',
    statement: 'Une présence digitale qui se comporte comme une pièce de direction artistique.',
    detail: 'Développement d’un univers éditorial sombre, pages expertises, offres, brief projet et architecture SEO.',
    tags: ['Web', 'Direction digitale', 'Next.js'],
  },
  {
    id: 'atelier',
    title: 'L’Atelier d’Héloïse',
    eyebrow: 'Boutique · prestations à réserver · gestion',
    image: '/projects-v2/atelier.webp',
    href: '/realisations/atelier-dheloise',
    external: false,
    tone: 'copper',
    statement: 'Un univers artistique riche, relié à un vrai outil de gestion derrière.',
    detail: 'Boutique, prise de rendez-vous pour les prestations, paiements, devis, factures et espace de gestion.',
    tags: ['Client réel', 'Django', 'E-commerce'],
  },
  {
    id: 'samass',
    title: 'SAMASS',
    eyebrow: 'Bien-être · rendez-vous clients',
    image: '/projects-v2/samass-new.webp',
    href: 'https://samassbysam.com',
    tone: 'sage',
    statement: 'Une expérience calme et sensorielle pour une activité où l’ambiance fait partie du service.',
    detail: 'Présentation des massages, prise de rendez-vous sur le site, disponibilités, emails et gestion côté praticien.',
    tags: ['Prise de RDV', 'Next.js', 'Django'],
  },
  {
    id: 'stockscan',
    title: 'StockScan',
    eyebrow: 'SaaS · inventaire',
    image: '/projects-v2/stockscan.webp',
    href: 'https://www.stockscan.app',
    tone: 'blue',
    statement: 'Un outil métier pensé pour faire gagner du temps sur le terrain.',
    detail: 'Scan code-barres, stock, DLC/DDM, pertes, exports, statistiques et logique métier.',
    tags: ['SaaS', 'React', 'Django REST'],
  },
];

function FlowBoard({ t }) {
  return (
    <div className="v2-flow-board">
      <div className="flow-board-top">
        <span>{t.boardLabel}</span>
        <span className="flow-live"><i /> LIVE</span>
      </div>
      <h2>{t.boardTitle}</h2>
      <div className="flow-canvas" aria-hidden="true">
        <div className="flow-line main" />
        <div className="flow-node n1"><span>01</span><strong>Client</strong><small>choisit un créneau</small></div>
        <div className="flow-node n2"><span>02</span><strong>Site</strong><small>vérifie la disponibilité</small></div>
        <div className="flow-node n3"><span>03</span><strong>Paiement</strong><small>acompte si utile</small></div>
        <div className="flow-node n4"><span>04</span><strong>Suivi</strong><small>confirmation automatique</small></div>
        <motion.i className="flow-pulse" animate={{ left: ['6%', '86%'] }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }} />
      </div>
      <p>{t.boardNote}</p>
    </div>
  );
}


/* Real professional profiles: placed next to contact and in the structured footer. */
function SocialLinks({ placement = 'footer' }) {
  return (
    <div className="v2-social-links">
      <a href="https://www.linkedin.com/in/alioune-b-seck-8b0002240/" target="_blank" rel="noopener noreferrer"
         onClick={() => trackEvent('external_profile_click', { platform: 'linkedin', placement })} aria-label="LinkedIn - Alioune Seck">
        <span className="v2-social-icon" aria-hidden="true">in</span><span>LinkedIn</span><span aria-hidden="true">↗</span>
      </a>
      <a href="https://github.com/Alioune4002" target="_blank" rel="noopener noreferrer"
         onClick={() => trackEvent('external_profile_click', { platform: 'github', placement })} aria-label="GitHub - Alioune4002">
        <svg className="v2-social-icon-svg" viewBox="0 0 24 24" role="img" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 19c-4.6 1.3-4.6-2.5-6.5-3.2M15 22v-4a3.6 3.6 0 0 0-.9-2.9c3-.3 6.2-1.4 6.2-6.6a5.1 5.1 0 0 0-1.4-3.6A4.9 4.9 0 0 0 18.8 1s-1.2-.4-3.8 1.4a13 13 0 0 0-6 0C6.4.6 5.2 1 5.2 1a4.9 4.9 0 0 0-.1 3.9 5.1 5.1 0 0 0-1.4 3.6c0 5.2 3.2 6.3 6.2 6.6A3.6 3.6 0 0 0 9 18v4"/>
        </svg>
        <span>GitHub</span><span aria-hidden="true">↗</span>
      </a>
    </div>
  );
}

export default function PortfolioV2() {
  const { lang, setLang } = useLang();
  const t = copy[lang] || copy.fr;
  const [activeProblem, setActiveProblem] = useState(0);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const formRef = useRef(null);
  const { register, handleSubmit, reset } = useForm();
  const active = t.problems[activeProblem];

  useEffect(() => {
    const titles = {
      fr: 'Alioune Seck | Développeur web freelance à Quimper',
      en: 'Alioune Seck | Freelance web developer in France',
      pt: 'Alioune Seck | Desenvolvedor web freelance na França',
    };
    const descriptions = {
      fr: 'Développeur web freelance à Quimper : sites sur mesure, prise de rendez-vous clients sur votre site, e-commerce, outils de gestion et reprise de projets.',
      en: 'Freelance web developer in France: custom websites, customer appointments booked on your site, e-commerce, business tools and project takeovers.',
      pt: 'Desenvolvedor web freelance na França: sites sob medida, agendamento de clientes no seu site, lojas online, ferramentas de gestão e retomada de projetos.',
    };
    document.title = titles[lang] || titles.fr;
    document.querySelector('meta[name="description"]')?.setAttribute('content', descriptions[lang] || descriptions.fr);
  }, [lang]);

  useEffect(() => {
    const saved = sessionStorage.getItem('prefilledContactMessage');
    if (!saved) return;
    reset({ message: saved });
    sessionStorage.removeItem('prefilledContactMessage');
    setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 250);
  }, [reset]);

  const onSubmit = () => {
    if (!formRef.current) return;
    const data = new FormData(formRef.current);
    if (data.get('honeypot')) return;
    setSending(true);
    trackEvent('contact_form_submit');

    const serviceId = 'service_u5azogu';
    const templateToYou = 'template_p284e2a';
    const templateAutoreply = 'template_ruoibdc';
    const publicKey = '7iv4FE3KzxDAAepum';

    emailjs.sendForm(serviceId, templateToYou, formRef.current, publicKey)
      .then(() => {
        setStatus('success');
        emailjs.send(serviceId, templateAutoreply, {
          name: data.get('name'),
          email: data.get('email'),
          phone: '',
          message: data.get('message'),
        }, publicKey).catch(() => {});
        reset();
        trackEvent('contact_form_success');
      })
      .catch(() => {
        setStatus('error');
        trackEvent('contact_form_error');
      })
      .finally(() => setSending(false));
  };

  const problemEstimateHref = useMemo(() => '/estimer-mon-projet?goal=' + active.id, [active.id]);

  return (
    <div className="portfolio-v2">
      <header className="v2-header">
        <a className="v2-brand" href="#top" aria-label="Alioune Seck">
          <img className="v2-brand-symbol" src="/brand/approved-logo-160.png" alt="" width="50" height="50" decoding="async" />
          <span><strong>Alioune Seck</strong><small>{t.role}</small></span>
        </a>

        <nav className="v2-nav">
          <a href="#work">{t.navWork}</a>
          <a href="#method">{t.navMethod}</a>
          <a href="#contact">{t.navContact}</a>
        </nav>

        <div className="v2-header-actions">
          <div className="v2-lang">
            {languages.map((code) => (
              <button type="button" key={code} className={lang === code ? 'active' : ''} onClick={() => setLang(code)}>{code.toUpperCase()}</button>
            ))}
          </div>
          <Link className="v2-header-cta" to="/estimer-mon-projet" onClick={() => trackEvent('cta_click', { placement: 'header', target: 'estimator' })}>
            <span className="v2-header-cta-full">{t.navEstimate}</span>
            <span className="v2-header-cta-short">{lang === 'fr' ? 'Estimer' : lang === 'pt' ? 'Estimar' : 'Estimate'}</span>
            <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </header>

      <main id="top">
        <section className="v2-hero">
          <div className="v2-hero-copy">
            <div className="v2-status-row">
              <span className="v2-location">{t.location}</span>
              <span className="v2-availability"><i />{t.availability}</span>
            </div>
            <span className="v2-kicker">{t.heroKicker}</span>
            <h1>{t.heroTitle}<span className="v2-hero-highlight">{t.heroPunchline}</span></h1>
            <p>{t.heroText}</p>
            <div className="v2-hero-actions">
              <a className="v2-primary" href="#contact" onClick={() => trackEvent('cta_click', { placement: 'hero', target: 'contact' })}>{t.heroPrimary} <span>→</span></a>
              <Link className="v2-secondary" to="/estimer-mon-projet" onClick={() => trackEvent('cta_click', { placement: 'hero_secondary', target: 'estimator' })}>{t.heroSecondary} <span aria-hidden="true">↗</span></Link>
            </div>
            <small className="v2-hero-foot">{t.heroFoot}</small>
          </div>
          <div className="v2-hero-visual"><FlowBoard t={t} /><div className="v2-hero-visual-signature" aria-hidden="true"><img src="/brand/approved-logo-160.png" alt="" /></div></div>
        </section>

        <section className="v2-problem-section">
          <div className="v2-section-heading">
            <span className="v2-kicker">{t.problemsKicker}</span>
            <h2>{t.problemsTitle}</h2>
            <p>{t.problemsIntro}</p>
          </div>

          <div className="problem-console">
            <div className="problem-list" role="tablist">
              {t.problems.map((problem, index) => (
                <button type="button" role="tab" aria-selected={activeProblem === index} className={activeProblem === index ? 'active' : ''} key={problem.id} onClick={() => {
                  setActiveProblem(index);
                  trackEvent('problem_selected', { problem: problem.id });
                }}>
                  <span>0{index + 1}</span>
                  <strong>{problem.title}</strong>
                </button>
              ))}
            </div>

            <motion.div className="problem-detail" key={active.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <span className="console-label">DIAGNOSTIC / {active.id.toUpperCase()}</span>
              <h3>{active.title}</h3>
              <p>{active.text}</p>
              <div className="mini-flow">
                {active.flow.map((item, index) => (
                  <React.Fragment key={item}>
                    <span>{item}</span>
                    {index < active.flow.length - 1 && <i>→</i>}
                  </React.Fragment>
                ))}
              </div>
              <strong className="problem-outcome">{active.outcome}</strong>
              <Link className="v2-inline-cta" to={problemEstimateHref}>Estimer ce type de projet →</Link>
            </motion.div>
          </div>
        </section>

        <section className="v2-work" id="work">
          <div className="v2-section-heading wide">
            <span className="v2-kicker">{t.workKicker}</span>
            <h2>{t.workTitle}</h2>
            <p>{t.workIntro}</p>
          </div>

          <div className="v2-projects">
            {projects.map((project, index) => (
              <motion.article
                className={'v2-project tone-' + project.tone + (index % 2 ? ' reverse' : '')}
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
              >
                <div className="project-visual">
                  <img src={project.image} alt={'Aperçu ' + project.title} loading="lazy" />
                  <span className="project-index">0{index + 1}</span>
                </div>
                <div className="project-story">
                  <span className="v2-kicker">{project.eyebrow}</span>
                  <h3>{project.title}</h3>
                  <blockquote>{project.statement}</blockquote>
                  <p>{project.detail}</p>
                  <div className="project-tags">{project.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                  {project.external === false ? (
                    <Link className="v2-inline-cta" to={project.href} onClick={() => trackEvent('project_click', { project: project.id })}>{t.projectCase} →</Link>
                  ) : (
                    <a className="v2-inline-cta" href={project.href} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('project_click', { project: project.id })}>{t.projectOpen} ↗</a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="v2-takeover" aria-labelledby="takeover-heading">
          <div className="v2-takeover-lead">
            <span className="v2-kicker">{t.takeoverKicker}</span>
            <h2 id="takeover-heading">{t.takeoverTitle}</h2>
            <p>{t.takeoverText}</p>
            <div className="v2-takeover-actions">
              <Link className="v2-primary" to="/services/reprise-projet-web" onClick={() => trackEvent('cta_click', { placement: 'takeover', target: 'takeover_service' })}>{t.takeoverPrimary} <span aria-hidden="true">↗</span></Link>
              <a className="v2-inline-cta" href="#contact" onClick={() => trackEvent('cta_click', { placement: 'takeover', target: 'contact' })}>{t.takeoverSecondary} →</a>
            </div>
          </div>
          <div className="v2-takeover-notes">
            {t.takeoverNotes.map(([title, text], index) => (
              <div className="v2-takeover-note" key={title}>
                <span>0{index + 1}</span>
                <div><h3>{title}</h3><p>{text}</p></div>
              </div>
            ))}
          </div>
        </section>

        <section className="v2-proof">
          <div className="v2-section-heading">
            <span className="v2-kicker">{t.proofKicker}</span>
            <h2>{t.proofTitle}</h2>
          </div>
          <div className="proof-grid">
            {t.proofItems.map(([title, text], index) => (
              <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{text}</p></article>
            ))}
          </div>
        </section>

        <section className="v2-reviews" aria-labelledby="client-reviews-title">
          <div className="v2-section-heading">
            <span className="v2-kicker">{t.reviewsKicker}</span>
            <div>
              <h2 id="client-reviews-title">{t.reviewsTitle}</h2>
              <p className="v2-reviews-intro">{t.reviewsIntro}</p>
            </div>
          </div>
          <div className="v2-review-grid">
            <figure className="v2-review-card">
              <span className="v2-review-stars" aria-label="5 sur 5">★★★★★</span>
              <blockquote>« Il explique très bien pour ceux qui ne sont pas du milieu. »</blockquote>
              <figcaption>
                <strong>Héloïse</strong>
                <span>L’Atelier d’Héloïse · 5 octobre 2026</span>
              </figcaption>
            </figure>
            <figure className="v2-review-card">
              <span className="v2-review-stars" aria-label="5 sur 5">★★★★★</span>
              <blockquote>« le travail est sérieux, soigné et correspond exactement à mes besoins. »</blockquote>
              <figcaption>
                <strong>Samuel</strong>
                <span>SAMASS · 11 décembre 2025</span>
              </figcaption>
            </figure>
          </div>
          <div className="v2-review-links">
            <a href="https://www.codeur.com/-aliounebp6ax" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('external_profile_click', { platform: 'codeur', placement: 'reviews' })}>{t.reviewsSource} ↗</a>
            <span>{t.reviewsProfiles}</span>
            <a href="https://www.malt.fr/profile/aliouneseck2" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('external_profile_click', { platform: 'malt', placement: 'reviews' })}>Malt ↗</a>
          </div>
        </section>

        <section className="v2-method" id="method">
          <div className="v2-section-heading">
            <span className="v2-kicker">{t.methodKicker}</span>
            <h2>{t.methodTitle}</h2>
          </div>
          <div className="method-track">
            {t.methodSteps.map(([number, title, text]) => (
              <article key={number}><span>{number}</span><h3>{title}</h3><p>{text}</p></article>
            ))}
          </div>
        </section>

        <section className="v2-estimate-callout">
          <div className="estimate-signal" aria-hidden="true">
            <span className="signal-ring r1" /><span className="signal-ring r2" /><span className="signal-ring r3" /><i />
          </div>
          <div>
            <span className="v2-kicker">{t.estimateKicker}</span>
            <h2>{t.estimateTitle}</h2>
            <p>{t.estimateText}</p>
            <Link className="v2-primary" to="/estimer-mon-projet" onClick={() => trackEvent('cta_click', { placement: 'midpage', target: 'estimator' })}>{t.estimateCta} →</Link>
          </div>
        </section>

        <section className="v2-about">
          <div className="about-portrait"><img src="/profile.jpg" alt="Alioune Seck" loading="lazy" /></div>
          <div>
            <span className="v2-kicker">{t.aboutKicker}</span>
            <h2>{t.aboutTitle}</h2>
            <p>{t.aboutText}</p>
            <small>{t.aboutSmall}</small>
          </div>
        </section>

        <section className="v2-contact" id="contact">
          <div className="v2-contact-copy">
            <span className="v2-kicker">{t.contactKicker}</span>
            <h2>{t.contactTitle}</h2>
            <p>{t.contactText}</p>
            <a href={'mailto:' + t.emailDirect}>{t.emailDirect}</a>
            <SocialLinks placement="contact" />
          </div>

          <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="v2-contact-form">
            <input type="text" name="honeypot" className="hp-field" {...register('honeypot')} tabIndex="-1" autoComplete="off" />
            <label><span>{t.name}</span><input name="name" {...register('name', { required: true })} required /></label>
            <label><span>{t.email}</span><input name="email" type="email" {...register('email', { required: true })} required /></label>
            <label><span>{t.message}</span><textarea name="message" rows="6" {...register('message', { required: true })} required /></label>
            <p className="v2-form-privacy">{t.privacyForm} <Link to="/politique-confidentialite">{t.privacyMore} →</Link></p>
            <button type="submit" className="v2-primary" disabled={sending}>{sending ? t.sending : t.send + ' →'}</button>
            {status && <p className={'v2-form-status ' + status}>{status === 'success' ? t.success : t.error}</p>}
          </form>
        </section>
      </main>


      <footer className="v2-footer">
        <div className="v2-footer-top">
          <div className="v2-footer-identity">
            <a className="v2-footer-signature" href="#top" aria-label="Retour à l'accueil - Alioune Seck">
              <img src="/brand/approved-logo-160.png" alt="" width="68" height="68" loading="lazy" decoding="async" />
              <span><strong>Alioune Seck</strong><small>{t.role}</small></span>
            </a>
            <p className="v2-footer-promise">{t.heroTitle} <em>{t.heroPunchline}</em></p>
            <span className="v2-footer-caption">{t.footer}</span>
          </div>
          <div className="v2-footer-links">
            <h2>{t.footerExplore}</h2>
            <nav aria-label={t.footerExplore}>
              <a href="#work">{t.navWork}</a>
              <a href="#method">{t.navMethod}</a>
              <Link to="/estimer-mon-projet">{t.navEstimate}</Link>
              <a href="#contact">{t.navContact}</a>
              <Link to="/developpeur-web-quimper">{t.localLink}</Link>
              <Link to="/developpeur-web-freelance-france">{t.nationalLink}</Link>
            </nav>
          </div>
          <div className="v2-footer-networks">
            <h2>{t.footerFind}</h2>
            <SocialLinks placement="footer" />
            <a href="https://www.malt.fr/profile/aliouneseck2" target="_blank" rel="noopener noreferrer"
               onClick={() => trackEvent('external_profile_click', { platform: 'malt', placement: 'footer' })}>Malt <span aria-hidden="true">↗</span></a>
            <a href="https://www.codeur.com/-aliounebp6ax" target="_blank" rel="noopener noreferrer"
               onClick={() => trackEvent('external_profile_click', { platform: 'codeur', placement: 'footer' })}>Codeur.com <span aria-hidden="true">↗</span></a>

          </div>
        </div>
        <div className="v2-footer-bottom">
          <nav aria-label={t.footerLegal}>
            <Link to="/mentions-legales">{t.legalMentions}</Link>
            <Link to="/politique-confidentialite">{t.legalPrivacy}</Link>
            <Link to="/cgu">{t.legalTerms}</Link>
            <Link to="/cgv">{t.legalSales}</Link>
            <button type="button" className="v2-footer-link-button" onClick={openAnalyticsPreferences}>{t.cookieSettings}</button>
          </nav>
          <small>© 2026 · SIREN 995 288 438</small>
        </div>
      </footer>
    </div>
  );
}
